import { clientCodeHeader } from "@entrilia/shared/api/clientApiContext";
import type { ApiError } from "@entrilia/shared/api/types";
import useFetch from "@entrilia/shared/hooks/useFetch";
import useSetInterval from "@entrilia/shared/hooks/useSetInterval";
import useSignalR from "@entrilia/shared/hooks/useSignalR";
import { logError } from "@entrilia/shared/logging";
import type { HubConnection } from "@microsoft/signalr";
import { useCallback, useEffect, useRef, useState } from "react";
import adminApi, { assistantHubUrl } from "../../../api/adminApi";
import useClientContext from "../../../context/useClientContext";
import { CHAT_UPDATED_EVENT, joinAssistantHub, RUN_UPDATED_EVENT, TEXT_PREVIEW_EVENT } from "./assistantHub";
import type { ChatGroup } from "./chatGroups";
import { clearChatUnreadUpdates, groupChatsByDate } from "./chatGroups";

const RELOAD_DELAY_MS = 3_000;
const NOT_CONNECTED_POLL_MS = 15_000;

const ignoreTextPreview = () => {};

interface AssistantChatList {
  chatGroups: ChatGroup[] | undefined;
  chatsError: ApiError | undefined;
  reloadChats: () => Promise<void>;
  reloadChatsAndMenuBadges: () => void;
  clearChatUnreadDot: (threadId: string) => void;
}

const useAssistantChatList = (): AssistantChatList => {
  const { updateMenuBadges } = useClientContext();
  const [chatGroups, setChatGroups] = useState<ChatGroup[]>();
  const reloadTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const [, chatsError, { fetchSilent }] = useFetch(adminApi.getAssistantChats, {
    onSetData: (chats) => setChatGroups(groupChatsByDate(chats, new Date())),
  });

  const reloadChats = useCallback(async () => {
    const error = await fetchSilent();
    if (error) {
      logError(error, "[useAssistantChatList] getAssistantChats");
    }
  }, [fetchSilent]);

  const reloadChatsAndMenuBadges = useCallback(() => {
    reloadChats();
    updateMenuBadges();
  }, [reloadChats, updateMenuBadges]);

  // useSignalR's debounceMs restarts on every notice, and saved text sends one about every second, so it would hold reloads until all answers stop.
  const scheduleReload = useCallback(() => {
    if (reloadTimerRef.current) {
      return;
    }
    reloadTimerRef.current = setTimeout(() => {
      reloadTimerRef.current = undefined;
      reloadChats();
    }, RELOAD_DELAY_MS);
  }, [reloadChats]);

  useEffect(() => () => clearTimeout(reloadTimerRef.current), []);

  const joinAndReload = useCallback(
    async (connection: HubConnection) => {
      await joinAssistantHub(connection);
      reloadChatsAndMenuBadges();
    },
    [reloadChatsAndMenuBadges]
  );

  const { isConnected } = useSignalR({
    url: assistantHubUrl,
    subscriptions: [
      { eventName: RUN_UPDATED_EVENT, callback: scheduleReload },
      { eventName: TEXT_PREVIEW_EVENT, callback: ignoreTextPreview },
      {
        eventName: CHAT_UPDATED_EVENT,
        callback: () => {
          updateMenuBadges();
          scheduleReload();
        },
      },
    ],
    customHeaders: clientCodeHeader(),
    onConnected: joinAndReload,
  });

  useSetInterval(reloadChatsAndMenuBadges, NOT_CONNECTED_POLL_MS, { disabled: isConnected });

  const clearChatUnreadDot = (threadId: string) =>
    setChatGroups((groups) => groups && clearChatUnreadUpdates(groups, threadId));

  return { chatGroups, chatsError, reloadChats, reloadChatsAndMenuBadges, clearChatUnreadDot };
};

export default useAssistantChatList;
